<template>
  <div class="Task p-3 p-md-4">
    <CreateGroupModal
      v-if="isGroupModalOpen"
      @close="isGroupModalOpen = false"
      @groupCreated="onGroupCreated"
    />

    <teleport to="body">
      <div class="modal fade" id="taskModal" tabindex="-1" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered modal-lg">
          <div class="modal-content border-0 shadow-lg rounded-4 overflow-hidden">
            <div class="modal-header border-0 p-4 text-white" :class="getHeaderClass(selectedTask?.status)">
              <h5 class="modal-title fw-bold">
                <i class="fas fa-tasks me-2"></i> {{ selectedTask?.title }}
              </h5>
              <button type="button" class="btn-close btn-close-white" data-bs-dismiss="modal" @click="closeDetails"></button>
            </div>

            <div class="modal-body p-0 bg-light">
              <div class="row g-0">
                <div class="col-md-5 p-4 border-end bg-white">
                  <label class="text-muted small fw-bold text-uppercase mb-2 d-block">Status & Timeline</label>
                  <div class="info-card p-3 rounded-3 border mb-3">
                    <span class="badge rounded-pill px-3 py-2" :class="getStatusBadgeClass(selectedTask?.status)">
                      {{ selectedTask?.status }}
                    </span>
                    <div class="mt-3 small text-muted">
                      <i class="far fa-calendar-alt me-1"></i> Created: {{ formatDate(selectedTask?.created_at) }}
                    </div>
                  </div>

                  <label class="text-muted small fw-bold text-uppercase mb-2 d-block">Assigned To</label>
                  <div class="d-flex align-items-center mb-4">
                    <div class="avatar-circle bg-primary text-white me-2 overflow-hidden d-flex align-items-center justify-content-center">
                      <img v-if="selectedTask?.assigned_profile || selectedTask?.creator_profile" 
                           :src="selectedTask?.assigned_profile || selectedTask?.creator_profile" 
                           class="w-100 h-100 object-fit-cover" />
                      <span v-else>{{ (selectedTask?.assigned_name || selectedTask?.creator_name || 'U').charAt(0).toUpperCase() }}</span>
                    </div>
                    <span class="fw-bold text-dark">{{ selectedTask?.assigned_name || selectedTask?.creator_name || "System User" }}</span>
                  </div>
                </div>

                <div class="col-md-7 p-4 bg-faded d-flex flex-column" style="max-height: 500px">
                  <label class="text-muted small fw-bold text-uppercase mb-3 d-block">
                    <i class="far fa-comments me-1"></i> Discussion
                  </label>

                  <div class="comments-list flex-grow-1 overflow-auto mb-3 pe-2" ref="commentsContainer">
                    <div v-if="isLoadingComments" class="text-center py-5">
                      <div class="spinner-border spinner-border-sm text-primary" role="status"></div>
                    </div>

                    <template v-else-if="comments.length > 0">
                      <div v-for="comment in comments" :key="comment.id" 
     class="comment-item mb-3 p-3 rounded-3 bg-white shadow-sm border-start border-3"
     :class="getBorderStyle(selectedTask?.status)">
  
  <div class="d-flex justify-content-between mb-1">
    <div>
      <span class="fw-bold small text-primary">{{ comment.username }}</span>
      <span class="text-muted extra-small ms-2">{{ formatCommentTime(comment.created_at) }}</span>
    </div>
    
    <button v-if="comment.user_id === currentUser.id" 
            @click="confirmDeleteComment(comment.id)" 
            class="btn btn-link text-danger p-0 border-0" 
            title="Delete comment">
      <i class="fas fa-trash-alt extra-small"></i>
    </button>
  </div>

  <p class="mb-0 small text-dark">{{ comment.content }}</p>
</div>
                      
                    </template>

                    <div v-else class="text-center py-5 text-muted">
                      <i class="fas fa-comment-slash d-block mb-2 fs-4 opacity-25"></i>
                      <span class="small">No comments yet. Start the conversation!</span>
                    </div>
                  </div>

                  <div class="comment-input-wrapper mt-auto">
                    <div class="input-group shadow-sm rounded-pill overflow-hidden bg-white border">
                      <input type="text" class="form-control border-0 ps-3 small" placeholder="Write a comment..." 
                             v-model="newComment" @keyup.enter="sendComment" :disabled="isSending" />
                      <button class="btn btn-primary px-3" @click="sendComment" :disabled="isSending || !newComment.trim()">
                        <i v-if="isSending" class="fas fa-spinner fa-spin"></i>
                        <i v-else class="fas fa-paper-plane"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </teleport>

    <div class="container text-center mb-4">
      <h1 class="display-6 font-weight-bold" v-if="username">
        Welcome, <span class="text-primary">{{ username }}</span>
        <i class="fas fa-smile-beam text-warning ml-2"></i>
      </h1>
      <p class="text-muted lead">Smartly manage your workflow and time.</p>
    </div>

    <div class="container mb-4">
      <div class="row align-items-end justify-content-center">
        <div class="col-12 col-md-5 col-lg-4 mb-3 mb-md-0">
          <WorkSpaceSwitcher ref="workspaceSwitcher" v-model="selectedGroupId" @members-updated="handleMembersUpdated" />
        </div>
        <div class="col-12 col-md-3 col-lg-2">
          <button @click="isGroupModalOpen = true" class="btn btn-outline-primary btn-block shadow-sm font-weight-bold" style="height: 52px">
            <i class="fas fa-plus-circle mr-1"></i> New Group
          </button>
        </div>
      </div>
    </div>

    <div class="container mb-5">
      <div class="add-task-wrapper shadow-sm p-3 bg-white rounded border">
        <div class="row align-items-center">
          <div class="col-12 col-md-4 mb-2 mb-md-0">
            <input type="text" class="form-control border-0 bg-light p-4" placeholder="What's your next move?" v-model="task" @keyup.enter="addTask" :disabled="isAdding" />
          </div>

          <div class="col-6 col-md-3 mb-2 mb-md-0" v-if="selectedGroupId">
            <select class="custom-select border-0 bg-light h-100" v-model="assignedTo" style="height: 50px !important">
              <option :value="null">Assign to...</option>
              <option v-for="member in groupMembers" :key="member.id" :value="member.id">{{ member.username }}</option>
            </select>
          </div>

          <div :class="selectedGroupId ? 'col-6 col-md-3' : 'col-7 col-md-5'">
            <select class="custom-select border-0 bg-light h-100" v-model="status" style="height: 50px !important">
              <option value="To do">To Do</option>
              <option value="in progress">In Progress</option>
              <option value="done">Done</option>
            </select>
          </div>

          <div class="col-12 col-md-2 mt-2 mt-md-0">
            <button class="btn btn-primary btn-block h-100 shadow-sm" @click="addTask" :disabled="!task.trim() || isAdding" style="height: 50px !important">
              <i v-if="isAdding" class="fas fa-spinner fa-spin"></i>
              <i v-else class="fas fa-plus"></i>
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="container mb-4">
      <div class="row justify-content-center">
        <transition name="smooth-fade">
          <div class="col-12 col-md-6" v-if="status === 'To do'">
            <div class="date-input-wrapper shadow-sm p-2 border rounded bg-white">
              <label class="text-muted font-weight-bold extra-small text-uppercase mb-1 d-block">
                <i class="fas fa-bell text-warning mr-1"></i> Reminder Date
              </label>
              <input type="date" class="form-control border-0 bg-light" v-model="startDate" />
            </div>
          </div>
        </transition>
      </div>
      <div class="mt-4">
        <search-on-task @search-results="handleSerachResult" :current-group-id="selectedGroupId" />
      </div>
    </div>

    <div class="row mt-4">
      <div class="col-lg-4 col-md-6 mb-4">
        <div class="column-card p-3 rounded bg-soft-secondary border shadow-sm">
          <h6 class="font-weight-bold mb-3 d-flex justify-content-between align-items-center">
            <span><i class="fas fa-list-ul mr-2"></i> TO DO</span>
            <span class="badge badge-dark badge-pill">{{ toDoTasks.length }}</span>
          </h6>
          <to-do :ToDoTasks="toDoTasks" @delete-task="deleteTask" @edit-task="updateTaskTitle" @show-detail="showDetails" />
          <div v-if="toDoTasks.length === 0" class="text-center p-4 text-muted small">No tasks yet.</div>
        </div>
      </div>

      <div class="col-lg-4 col-md-6 mb-4">
        <div class="column-card p-3 rounded bg-soft-primary border shadow-sm">
          <h6 class="font-weight-bold mb-3 d-flex justify-content-between align-items-center text-primary">
            <span><i class="fas fa-spinner fa-spin mr-2"></i> IN PROGRESS</span>
            <span class="badge badge-primary badge-pill">{{ inProgressTasks.length }}</span>
          </h6>
          <in-progress :InProgressTasks="inProgressTasks" @delete-task="deleteTask" @show-detail="showDetails" />
          <div v-if="inProgressTasks.length === 0" class="text-center p-4 text-muted small">Nothing in progress.</div>
        </div>
      </div>

      <div class="col-lg-4 col-md-12 mb-4">
        <div class="column-card p-3 rounded bg-soft-success border shadow-sm">
          <h6 class="font-weight-bold mb-3 d-flex justify-content-between align-items-center text-success">
            <span><i class="fas fa-check-circle mr-2"></i> DONE</span>
            <span class="badge badge-success badge-pill">{{ doneTasks.length }}</span>
          </h6>
          <done :DoneTasks="doneTasks" @delete-task="deleteTask" @show-detail="showDetails" />
          <div v-if="doneTasks.length === 0" class="text-center p-4 text-muted small">No completed tasks.</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ToDo from "@/components/ToDo.vue";
import InProgress from "@/components/InProgress.vue";
import Done from "@/components/Done.vue";
import { tasksService } from "@/services/tasksService";
import { groupsService } from "@/services/groupsService";
import Swal from "sweetalert2";
import SearchOnTask from "@/components/SearchOnTask.vue";
import WorkSpaceSwitcher from "@/components/WorkSpaceSwitcher.vue";
import CreateGroupModal from "@/components/CreateGroupModal.vue";

export default {
  name: "TasklyView",
  components: { ToDo, InProgress, Done, SearchOnTask, WorkSpaceSwitcher, CreateGroupModal },

  data() {
    const userData = JSON.parse(localStorage.getItem("user") || "{}");
    return {
      username: userData.username,
      task: "",
      status: "To do",
      allTasks: [],
      startDate: new Date().toISOString().split("T")[0],
      selectedTask: null,
      filteredTasks: null,
      selectedGroupId: null,
      isGroupModalOpen: false,
      comments: [],
      newComment: "",
      isLoadingComments: false,
      isSending: false,
      isAdding: false, // Added loading state for button UX
      assignedTo: null,
      groupMembers: [],
    };
  },

  computed: {
    // Determine the active data source for rendering
    currentDataSource() {
      return this.filteredTasks !== null ? this.filteredTasks : this.allTasks;
    },
    toDoTasks() {
      return this.currentDataSource.filter(t => t.status.toLowerCase() === "to do");
    },
    inProgressTasks() {
      return this.currentDataSource.filter(t => t.status.toLowerCase() === "in progress");
    },
    doneTasks() {
      return this.currentDataSource.filter(t => t.status.toLowerCase() === "done");
    },
    currentUser() {
      return this.$store.state.user;
    },
    username() {
    return this.$store.state.user.username || JSON.parse(localStorage.getItem("user")).username;
  }
  },

  watch: {
    // Auto-fetch tasks when workspace changes
    selectedGroupId() {
      this.fetchTasks();
      this.assignedTo = null;
      this.groupMembers = [];
    },
  },

  methods: {
    /** * Handles search results from search component
     */
    handleSerachResult(results) {
      this.filteredTasks = results;
    },

    /** * Fetches tasks from server and triggers UI update
     */
    async fetchTasks() {
      try {
        const data = await tasksService.getAllTasks(this.selectedGroupId);
        // Using spread to ensure Vue detects array change
        this.allTasks = [...data];
        this.$store.dispatch("getNotifications");
      } catch (error) {
        throw error;
      }
    },

    /** * Syncs members list from switcher component
     */
    handleMembersUpdated(members) {
      this.groupMembers = members;
    },

    /** * Adds a new task with immediate UI refresh
     */
   
    async addTask() {
  if (!this.task.trim()) return;

  try {
    
    if (this.selectedGroupId) {
      const groups = await groupsService.getMyGroups();
      const currentGroup = groups.find(g => g.id === this.selectedGroupId);
      const currentUserId = this.$store.state.user?.id || JSON.parse(localStorage.getItem("user"))?.id;

      
      if (currentGroup && currentGroup.owner_id !== currentUserId) {
        Swal.fire("Access Denied", "Only the group owner can add tasks.", "warning");
        return;  
      }
    }

    
    this.isAdding = true;
    const dateToSend = this.status.toLowerCase() === "to do" ? this.startDate : null;

    const taskData = {
      title: this.task,
      status: this.status,
      start_date: dateToSend,
      group_id: this.selectedGroupId,
      assigned_to: this.assignedTo  
    };

    await tasksService.addTask(taskData);

    
    this.task = "";
    this.assignedTo = null;
    
    
    this.filteredTasks = null;
    await this.fetchTasks(); 

    Swal.fire({
      icon: "success",
      title: "Added!",
      timer: 1000,
      showConfirmButton: false,
    });

  } catch (error) {
    console.error("Error in addTask:", error);
    Swal.fire("Error", "Could not connect to server", "error");
  } finally {
    this.isAdding = false;
  }
},

async confirmDeleteComment(commentId) {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    });
    if (result.isConfirmed) {
      this.deleteComment(commentId);
    }
  },

  
  async deleteComment(commentId) {
    try {
       
      await tasksService.deleteComment(commentId);
      
      
      this.comments = this.comments.filter(c => c.id !== commentId);

      Swal.fire({
        icon: 'success',
        title: 'Deleted!',
        text: 'Your comment has been deleted.',
        timer: 1500,
        showConfirmButton: false
      });
    } catch (error) {
      Swal.fire('Error', 'Failed to delete comment', 'error');
    }
  },
 
    /** * Deletes a task and refreshes the list
     */
    async deleteTask(taskId) {
      const result = await Swal.fire({
        title: "Are you sure?",
        text: "You won't be able to revert this!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        confirmButtonText: "Yes, delete it!",
      });

      if (result.isConfirmed) {
        try {
          await tasksService.deleteTask(taskId);
          await this.fetchTasks(); // Force immediate update
          this.$store.dispatch("getNotifications");
        } catch (error) {
          Swal.fire("Error", "Could not delete task", "error");
        }
      }
    },

    /** * Handles task title/status updates via popup
     */
    async updateTaskTitle(index, taskId) {
      const taskToEdit = this.toDoTasks[index];
      const { value: formValues } = await Swal.fire({
        title: "Edit Task",
        html: `
          <input id="swal-input1" class="swal2-input" value="${taskToEdit.title}">
          <select id="swal-input2" class="swal2-select w-100">
            <option value="To do" ${taskToEdit.status.toLowerCase() === "to do" ? "selected" : ""}>To do</option>
            <option value="in progress" ${taskToEdit.status.toLowerCase() === "in progress" ? "selected" : ""}>In Progress</option>
            <option value="done" ${taskToEdit.status.toLowerCase() === "done" ? "selected" : ""}>Done</option>
          </select>`,
        showCancelButton: true,
        preConfirm: () => ({
          title: document.getElementById("swal-input1").value,
          status: document.getElementById("swal-input2").value,
        }),
      });

      if (formValues) {
        try {
          await tasksService.updateTask(taskId, formValues);
          await this.fetchTasks(); // Force immediate update
          Swal.fire({ icon: "success", title: "Updated!", timer: 1000, showConfirmButton: false });
        } catch (error) {
          Swal.fire("Error", "permission dined", "error");
        }
      }
    },

    /** * Modal logic to show task details and comments
     */
    async showDetails(task) {
      this.selectedTask = task;
      this.comments = [];
      window.$("#taskModal").modal("show");
      await this.fetchComments(task.id);
    },

    /** * Fetch comments from server for the specific task
     */
    async fetchComments(taskId) {
      this.isLoadingComments = true;
      try {
        const res = await tasksService.getTaskComments(taskId);
        this.comments = res || [];
        this.scrollToBottom();
      } catch (error) {
        console.error("Error fetching comments:", error);
      } finally {
        this.isLoadingComments = false;
      }
    },

    /** * Post a new comment and scroll to bottom
     */
    async sendComment() {
      if (!this.newComment.trim() || !this.selectedTask) return;
      this.isSending = true;
      try {
        const commentData = {
          taskId: this.selectedTask.id,
          content: this.newComment,
        };

        await tasksService.addComment(commentData);
        this.newComment = "";
        await this.fetchComments(this.selectedTask.id);
      } catch (error) {
        Swal.fire("Error", "Could not post your comment", "error");
      } finally {
        this.isSending = false;
      }
    },

    scrollToBottom() {
      this.$nextTick(() => {
        const container = this.$refs.commentsContainer;
        if (container) container.scrollTop = container.scrollHeight;
      });
    },

    formatCommentTime(dateString) {
      if (!dateString) return "N/A";
      return new Date(dateString).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    },

    formatDate(dateString) {
      if (!dateString) return "N/A";
      return new Date(dateString).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
    },

    getHeaderClass(status) {
      const s = status?.toLowerCase();
      return s === "done" ? "bg-success" : s === "in progress" ? "bg-primary" : "bg-secondary";
    },

    getStatusBadgeClass(status) {
      const s = status?.toLowerCase();
      return s === "done" ? "badge-success" : s === "in progress" ? "badge-primary" : "badge-secondary";
    },

    getBorderStyle(status) {
      const s = status?.toLowerCase();
      return s === "done" ? "border-success" : s === "in progress" ? "border-primary" : "border-secondary";
    },

    onGroupCreated() {
      if (this.$refs.workspaceSwitcher) this.$refs.workspaceSwitcher.fetchGroups();
      this.isGroupModalOpen = false;
    },

    closeDetails() {
      window.$("#taskModal").modal("hide");
      this.selectedTask = null;
    },
  },

  mounted() {
    if (!localStorage.getItem("token")) {
      this.$router.push("/auth");
    } else {
      this.fetchTasks();
      this.$store.dispatch("getNotifications");
      this.$store.dispatch('getCurrentUser');
    }
  },
};
</script>

<style scoped lang="scss">
/* Optimized UI styles for better Kanban experience */
.avatar-circle {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  font-weight: bold;
}

.comments-list {
  scrollbar-width: thin;
  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-thumb { background: #dee2e6; border-radius: 10px; }
}

.comment-item {
  transition: transform 0.2s;
  &:hover { transform: translateX(5px); }
}

.bg-faded { background-color: #f8f9fa; }

.Task {
  background-color: #f4f7f6;
  min-height: 100vh;
}

.add-task-wrapper {
  max-width: 900px;
  margin: 0 auto;
}

.column-card {
  min-height: 500px;
  transition: all 0.3s ease;
}

/* Color palettes for the columns */
.bg-soft-secondary { background-color: #ebedef; }
.bg-soft-primary { background-color: #e8f0fe; }
.bg-soft-success { background-color: #e6f4ea; }

.extra-small { font-size: 0.7rem; }

/* Smooth transitions for reminder date input */
.smooth-fade-enter-active, .smooth-fade-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}
.smooth-fade-enter-from, .smooth-fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
</style>